import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Auth as Authorization } from 'aws-amplify';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { Platform } from "react-native";

import * as LoginActions from '../redux/actions/loginAction';
import * as AppActions from '../redux/actions/appAction';
import * as SettingsActions from '../redux/actions/settingsAction';

import { AppUserForGrainAnalysisStack } from '../stack/AppUserForGrainAnalysisStack';
import { AppViewOnlyUserStack } from '../stack/AppViewOnlyUserStack';
import {MlStack} from 'src/stack/MlStack'; // Import the correct component
import { StackChooser } from '../stack/StackChooser';
import { IStore } from '../redux/reducers';
import { OrgAdminStack } from '../stack/OrgAdminStack';
import { LoginStack } from '../stack/LoginStack';
import { OrgSuperAdminStack } from 'src/stack/OrgSuperAdminStack';
import remoteConfig from '@react-native-firebase/remote-config';
import { isNative } from 'src/utils/utils';
import { defaultStackForGroup } from 'src/stack/screens';
import { NativeBaseProvider } from "native-base";
import ProgressBar from '../components/ProgressBar';
import MlRuntimeSetupScreen, { MlRuntimeInstallStage } from '../components/MlRuntimeSetupScreen';
import { nativeBaseTheme } from '../theme';
import { setUser, addMetadata, leaveBreadcrumb } from '../utils/tracker';
import { RELEASE_STAGE } from '../constants';
import { getText } from '../utils/i18n';
import { isElectron } from 'src/utils/miscUtils';
import { Button, Modal, Text, VStack } from 'native-base';
import { isOTAUpdatesEnabled } from '../config/otaConfig';
import { logDesktopAuthEvent } from '../utils/desktopLog';
import { EXPLICIT_LOGOUT_STORAGE_KEY } from '../constants';
import { isMobileSupportedGroup, isMobileSupportedStack } from '../utils/mobileRoles';

export const App = () => {
    const userLoggedIn = useSelector((store: IStore) => store.login.userLoggedIn);
    const currentStack = useSelector((store: IStore) => store.app.currentStack);
    const userGroups = useSelector((store: IStore) => store.user.userProfile.response?.groups);
    const userProfile = useSelector((store: IStore) => store.user.userProfile.response);
    const userProfileError = useSelector((store: IStore) => store.user.userProfile.error);
    const isOnline = useSelector((store: IStore) => store.app.isOnline);
    const cloudAuth = useSelector((store: IStore) => store.app.cloudAuth);
    const colorMode = useSelector((store: IStore) => store.settings.colorMode);
    const locale = useSelector((store: IStore) => store.app.locale);
    const preferredProcessingMode = useSelector((store: IStore) => store.settings.preferredProcessingMode);

    const [showLoginStack, setShowLoginStack] = React.useState(true);
    const needsOnlineAuthRecheckRef = React.useRef(false);

    // null = not yet checked, true = ready, false = needs install
    const [mlRuntimeReady, setMlRuntimeReady] = React.useState<boolean | null>(
        isElectron() && (typeof window !== 'undefined' && (window as any)?.electron?.platform === 'win32') ? null : true
    );
    const [mlInstallStage, setMlInstallStage] = React.useState<MlRuntimeInstallStage>('checking');
    const [mlInstallError, setMlInstallError] = React.useState<string | null>(null);

    const dispatch = useDispatch();
    const theme = useMemo(() => nativeBaseTheme(colorMode), [colorMode]);
    const showProcessingModeModal = isElectron() && preferredProcessingMode === undefined;

    useEffect(() => {
        if (Platform.OS !== 'web' || typeof document === 'undefined') return;
        document.documentElement.dataset.colorMode = colorMode;
        document.documentElement.lang = locale || 'en';
    }, [colorMode, locale]);

    const processingModeModal = (
        <Modal isOpen={showProcessingModeModal}>
            <Modal.Content maxWidth="400px">
                <Modal.Header>{getText('processingMode')}</Modal.Header>
                <Modal.Body>
                    <VStack space={3}>
                        <Text>{getText('processingModeDescription')}</Text>
                        <Button onPress={() => dispatch(SettingsActions.setPreferredProcessingMode('local'))}>
                            {getText('processingModeLocal')}
                        </Button>
                        <Button onPress={() => dispatch(SettingsActions.setPreferredProcessingMode('cloud'))} variant="outline">
                            {getText('processingModeCloud')}
                        </Button>
                    </VStack>
                </Modal.Body>
            </Modal.Content>
        </Modal>
    );

    const withProvider = (children: React.ReactNode, key?: string) => (
        <NativeBaseProvider theme={theme} key={key || `theme-${colorMode}`}>
            {children}
            {processingModeModal}
        </NativeBaseProvider>
    );

    useEffect(() => {
        if (userLoggedIn === "LOGGED_IN" && (currentStack === 'stackChooser' || !currentStack) && userGroups && userGroups.length === 1) {
            const primaryGroup = userGroups[0];
            const stackToUse = Platform.OS === 'web' || isMobileSupportedGroup(primaryGroup)
                ? defaultStackForGroup(primaryGroup)
                : 'stackChooser';
            if (stackToUse && stackToUse !== 'stackChooser') {
                dispatch(AppActions.setCurrentStack(stackToUse));
            }
        }
    }, [userLoggedIn, currentStack, userGroups, dispatch]);

    const getBrowserOnline = (): boolean | undefined => {
        try {
            if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
                return navigator.onLine;
            }
        } catch {
            // ignore
        }
        return undefined;
    };

    const isLikelyAuthRevocation = (error: any): boolean => {
        const name = String(error?.name || error?.code || '');
        const message = String(error?.message || '');
        if (name.includes('NotAuthorizedException')) return true;
        if (message.includes('NotAuthorizedException')) return true;
        if (message.toLowerCase().includes('not authorized')) return true;
        if (message.includes('Access Token has been revoked')) return true;
        if (message.toLowerCase().includes('token') && message.toLowerCase().includes('revok')) return true;
        if (message.toLowerCase().includes('refresh token') && message.toLowerCase().includes('expire')) return true;
        return false;
    };

    const wasExplicitlyLoggedOut = async (): Promise<boolean> => {
        try {
            return (await AsyncStorage.getItem(EXPLICIT_LOGOUT_STORAGE_KEY)) === '1';
        } catch {
            return false;
        }
    };

    const clearExplicitLogoutMarker = async () => {
        try {
            await AsyncStorage.removeItem(EXPLICIT_LOGOUT_STORAGE_KEY);
        } catch {
            // ignore
        }
    };

    const checkAuthToken = async () => {
        const browserOnline = getBrowserOnline();
        const onlineNow = isOnline !== false && (browserOnline !== undefined ? browserOnline : true);
        const hasCachedProfile = !!userProfile?.username;
        const explicitLogoutRequested = await wasExplicitlyLoggedOut();

        if (explicitLogoutRequested) {
            void logDesktopAuthEvent('currentAuthenticatedUser.skipped_explicit_logout', {
                hasCachedProfile,
                onlineNow,
            });
            setShowLoginStack(true);
            dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
            return;
        }

        // Electron offline mode: if the user has previously logged in on this machine,
        // allow app startup offline without forcing a Cognito session check.
        if (isElectron() && onlineNow === false && hasCachedProfile) {
            void logDesktopAuthEvent('currentAuthenticatedUser.skipped_offline_cached_profile', {
                hasCachedProfile,
                onlineNow,
            });
            needsOnlineAuthRecheckRef.current = true;
            setShowLoginStack(false);
            dispatch(LoginActions.setUserLoggedIn("LOGGED_IN"));
            return;
        }

        void logDesktopAuthEvent('currentAuthenticatedUser.start', {
            hasCachedProfile,
            onlineNow,
        });
        Authorization.currentAuthenticatedUser().then((user) => {
            if (user !== undefined) {
                void clearExplicitLogoutMarker();
                void logDesktopAuthEvent('currentAuthenticatedUser.success', {
                    hasCachedProfile,
                });
                setShowLoginStack(false)
                dispatch(LoginActions.setUserLoggedIn("LOGGED_IN"))
                dispatch(AppActions.setCloudAuthStatus({
                    status: 'valid',
                    reason: undefined,
                    updatedAt: Date.now(),
                }));
            }
        }).catch((e) => {
            console.log('error', e)
            void logDesktopAuthEvent('currentAuthenticatedUser.failure', {
                hasCachedProfile,
                onlineNow,
                errorName: e?.name || e?.code,
                errorMessage: e?.message,
            });
            if (isElectron() && hasCachedProfile) {
                setShowLoginStack(false);
                dispatch(LoginActions.setUserLoggedIn("LOGGED_IN"));
                if (onlineNow && isLikelyAuthRevocation(e)) {
                    dispatch(AppActions.setCloudAuthStatus({
                        status: 'invalid',
                        reason: 'auth_check_failed',
                        updatedAt: Date.now(),
                    }));
                }
                return;
            }
            setShowLoginStack(true);
            dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
        })
    }

    // If we booted offline using cached state, re-validate auth once we're back online.
    useEffect(() => {
        if (!isElectron()) return;
        if (!needsOnlineAuthRecheckRef.current) return;
        const browserOnline = getBrowserOnline();
        const onlineNow = isOnline !== false && (browserOnline !== undefined ? browserOnline : true);
        if (!onlineNow) return;

        needsOnlineAuthRecheckRef.current = false;
        Authorization.currentAuthenticatedUser()
            .then((user) => {
                if (user !== undefined) {
                    void clearExplicitLogoutMarker();
                    void logDesktopAuthEvent('currentAuthenticatedUser.recheck_success', {
                        hasCachedProfile: !!userProfile?.username,
                    });
                    setShowLoginStack(false);
                    dispatch(LoginActions.setUserLoggedIn("LOGGED_IN"));
                    dispatch(AppActions.setCloudAuthStatus({
                        status: 'valid',
                        reason: undefined,
                        updatedAt: Date.now(),
                    }));
                }
            })
            .catch((e) => {
                const hasCachedProfile = !!userProfile?.username;
                void logDesktopAuthEvent('currentAuthenticatedUser.recheck_failure', {
                    hasCachedProfile,
                    errorName: e?.name || e?.code,
                    errorMessage: e?.message,
                });
                if (hasCachedProfile) {
                    setShowLoginStack(false);
                    dispatch(LoginActions.setUserLoggedIn("LOGGED_IN"));
                    if (isLikelyAuthRevocation(e)) {
                        dispatch(AppActions.setCloudAuthStatus({
                            status: 'invalid',
                            reason: 'auth_check_failed',
                            updatedAt: Date.now(),
                        }));
                    }
                    return;
                }
                setShowLoginStack(true);
                dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
            });
    }, [isOnline, dispatch, userProfile]);

    useEffect(() => {
        if (isElectron() && cloudAuth?.status === 'invalid') {
            return;
        }
        if (userLoggedIn === "LOGGED_IN" && (!userGroups || userGroups.length === 0)) {
            const UserActions = require('../redux/actions/userAction');
            dispatch(UserActions.getUserProfile.request());
        }
    }, [userLoggedIn, userGroups, dispatch, cloudAuth?.status]);

    useEffect(() => {
        if (userLoggedIn !== "LOGGED_IN") return;
        if (userProfile?.username) return;
        if (!userProfileError) return;

        // On Electron, a transient profile fetch failure should not bounce a
        // previously authenticated desktop user back to the login screen.
        if (isElectron()) {
            void logDesktopAuthEvent('profile_bootstrap_failure.kept_desktop_session', {
                errorName: userProfileError?.name,
                errorMessage: userProfileError?.message,
            });
            return;
        }

        // Avoid a permanent loading screen when profile fetch fails.
        setShowLoginStack(true);
        dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
    }, [userLoggedIn, userProfile, userProfileError, dispatch]);

    // Track user context in Bugsnag
    useEffect(() => {
        if (userLoggedIn === "LOGGED_IN" && userProfile) {
            // Set user identification
            setUser(
                userProfile.username,
                userProfile.username, // Use username as email placeholder
                userProfile.username
            );

            // Add user metadata for error context
            addMetadata('user', {
                username: userProfile.username,
                organizationId: userProfile.org?.orgId,
                organizationName: userProfile.org?.name,
                centerId: userProfile.center?.centerId,
                centerName: userProfile.center?.name,
                groups: userGroups?.join(', '),
                grainsCount: userProfile.grains?.length || 0,
            });

            // Add app context
            addMetadata('app', {
                currentStack: currentStack,
                platform: Platform.OS,
                colorMode: colorMode,
                releaseStage: RELEASE_STAGE(),
            });

            leaveBreadcrumb('User logged in', {
                username: userProfile.username,
                organizationId: userProfile.org?.orgId,
                groups: userGroups?.join(', '),
            }, 'state');
        }
    }, [userLoggedIn, userProfile, currentStack, colorMode, userGroups]);

    useEffect(() => {
        if (userLoggedIn === "UNKNOWN") {
            checkAuthToken()
        } else if (userLoggedIn === "LOGGED_IN") {
            setShowLoginStack(false)
        } else if (userLoggedIn === "NOT_LOGGED_IN") {
            setShowLoginStack(true)
        }
        setStack()
    }, [currentStack, userLoggedIn]);

    useEffect(() => {
        initRemoteConfig()
    }, []);

    const initRemoteConfig = async () => {
        if (!isNative()) {
            return
        }
        remoteConfig()
            .setDefaults({
                api_cache_expiration_duration: 86400,
                api_inference_jobs_polling_delay: 10,
                api_retry_limit_on_error: 1,
                api_inference_attemps_on_empty_results: 3,
                api_inference_page_interval: 604800,
                api_inference_refresh_interval: 86400
            })
            .then(() => remoteConfig().fetchAndActivate())
            .then(fetchedRemotely => {
                if (fetchedRemotely) {
                    console.log('Configs were retrieved from the backend and activated.');
                } else {
                    console.log(
                        'No configs were fetched from the backend, and the local configs were already activated',
                    );
                }
            });
    }

    useEffect(() => {
        if (userLoggedIn !== "LOGGED_IN") return;
        if (!isOTAUpdatesEnabled()) return;
        if (!(isNative() || isElectron())) return;
        if (isElectron() && cloudAuth?.status === 'invalid') return;
        const browserOnline = getBrowserOnline();
        const onlineNow = isOnline !== false && (browserOnline !== false);
        if (!onlineNow) return;

        // Import lazily to keep OTA code out of critical path when disabled.
        const UpdateActions = require('../redux/actions/updateAction');
        dispatch(UpdateActions.checkUpdates.request());
    }, [userLoggedIn, isOnline, dispatch, cloudAuth?.status]);

    // Windows Electron: check and install bundled CPU ML runtime at login, blocking the app until ready.
    useEffect(() => {
        if (userLoggedIn !== "LOGGED_IN") return;
        if (!isElectron()) return;
        const platform = (typeof window !== 'undefined' && (window as any)?.electron?.platform) || null;
        if (platform !== 'win32') return;

        const electronAPI = typeof window !== 'undefined' ? (window as any)?.electronAPI : null;
        if (!electronAPI?.ensureMlRuntimeInstalled || !electronAPI?.getMlRuntimeStatus) return;

        let cancelled = false;

        const unsubscribe = electronAPI.onMlRuntimeInstallProgress?.((event: { stage: string; error?: string }) => {
            if (cancelled) return;
            if (event.stage === 'staging' || event.stage === 'extracting') {
                setMlInstallStage(event.stage as MlRuntimeInstallStage);
            } else if (event.stage === 'done') {
                setMlInstallStage('done');
                setMlRuntimeReady(true);
            } else if (event.stage === 'error') {
                setMlInstallStage('error');
                setMlInstallError(event.error || 'Installation failed');
            }
        });

        (async () => {
            try {
                setMlInstallStage('checking');
                const status = await electronAPI.getMlRuntimeStatus();
                if (cancelled) return;

                if (status?.success && (status.cpuInstalled || status.cudaInstalled)) {
                    setMlRuntimeReady(true);
                    return;
                }

                // Neither CPU nor CUDA is installed — show blocking screen and install CPU.
                setMlRuntimeReady(false);
                setMlInstallStage('staging');
                const resp = await electronAPI.ensureMlRuntimeInstalled({ kind: 'cpu', source: 'bundled' });
                if (cancelled) return;

                if (resp?.success) {
                    setMlRuntimeReady(true);
                } else {
                    setMlInstallStage('error');
                    setMlInstallError(resp?.error || 'ML runtime installation failed');
                }
            } catch (error: any) {
                if (cancelled) return;
                setMlInstallStage('error');
                setMlInstallError(error?.message || 'ML runtime installation failed');
            }
        })();

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [userLoggedIn]);

    const handleMlRuntimeRetry = React.useCallback(() => {
        const electronAPI = typeof window !== 'undefined' ? (window as any)?.electronAPI : null;
        if (!electronAPI?.ensureMlRuntimeInstalled) return;
        setMlInstallStage('staging');
        setMlInstallError(null);
        electronAPI.ensureMlRuntimeInstalled({ kind: 'cpu', source: 'bundled' }).then((resp: any) => {
            if (resp?.success) {
                setMlRuntimeReady(true);
            } else {
                setMlInstallStage('error');
                setMlInstallError(resp?.error || 'ML runtime installation failed');
            }
        }).catch((error: any) => {
            setMlInstallStage('error');
            setMlInstallError(error?.message || 'ML runtime installation failed');
        });
    }, []);

    const setStack = () => {
        if (showLoginStack) {
            return (
                withProvider(<LoginStack />)
            )
        }

        // ML runtime check/install gate (Windows Electron only)
        if (userLoggedIn === "LOGGED_IN" && mlRuntimeReady !== true) {
            return withProvider(
                mlRuntimeReady === false
                    ? <MlRuntimeSetupScreen
                        stage={mlInstallStage}
                        error={mlInstallError}
                        onRetry={mlInstallStage === 'error' ? handleMlRuntimeRetry : undefined}
                      />
                    : <ProgressBar isVisible={true} progressText={getText('loading')} progressSubText="Checking ML runtime..." />
            );
        }

        if (userLoggedIn === "LOGGED_IN" && (!userGroups || userGroups.length === 0)) {
            if (userProfileError && !isElectron()) {
                return (
                    withProvider(<LoginStack />)
                );
            }
            return (
                withProvider(<ProgressBar isVisible={true} progressText={getText('loading')} progressSubText={getText('loadingProfile')} />)
            );
        }

        if (userLoggedIn === "UNKNOWN") {
            return (
                withProvider(<ProgressBar isVisible={true} progressText={getText('loading')} progressSubText={getText('loadingAuth')} />)
            );
        }

        if (Platform.OS === 'web') {
            return (
                <NativeBaseProvider theme={theme} key={`theme-${colorMode}`}>
                    {currentStack === 'orgAdmin' && <OrgAdminStack key="orgAdmin" />}
                    {currentStack === 'orgSuperAdmin' && <OrgSuperAdminStack key="orgSuperAdmin" />}
                    {currentStack === 'appUser' && <AppUserForGrainAnalysisStack key="appUser" />}
                    {currentStack === 'appViewOnlyUser' && <AppViewOnlyUserStack key="appViewOnlyUser" />}
                    {currentStack === 'mlAdmin' && <MlStack key="mlAdmin" />}
                    {!currentStack || currentStack === 'stackChooser' ? <StackChooser key="stackChooser" /> : null}
                    {processingModeModal}
                </NativeBaseProvider>
            );
        } else {
            if (!isMobileSupportedStack(currentStack)) {
                return withProvider(<StackChooser />, `theme-${colorMode}`);
            }
            return (
                withProvider(currentStack === 'appViewOnlyUser' ? <AppViewOnlyUserStack /> : <AppUserForGrainAnalysisStack />, `theme-${colorMode}`)
            );
        }
    }

    return setStack();
}
